import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import { Chord } from '../types/chord';
import { Note, NoteSet } from '../types/note';
import { checkChordMatch } from '../utils/chordUtils';
import logger from '../utils/logger';
import { useMidiContext } from './MidiContext';
import { useVirtualKeyboard } from './VirtualKeyboardContext';
import { useUserContext } from './UserContext';
import { useSettings } from './SettingsContext';
import { TASK_QUEUE_SIZE, MIN_REMAINING_TASKS, ANIMATION_INTERVAL_MS, MAX_EXP, EXP_DROP_RATE_MIN, EXP_DROP_RATE_MAX, EXP_FREEZE_DURATION } from '../constants';

import { enableMapSet, produce } from 'immer';
enableMapSet();

interface Task {
  chord: Chord;
  taskId: number;
  chordIndex: number;
  level: number;

  startTime: number;
  isFailed: boolean;
  isClosed: boolean;
}

interface TaskResponse {
  id: number;
  chords: string;
  chord_count: number;
  level: number;
}

interface GameContextType {
  tasks: Map<number, Task>;
  taskEnd: number;
  taskIndex: number;

  exp: number;
  level: number;

  moveForward: () => void;
  setLevel: (level: number) => void; // Add this line
}

const LEVEL_STORAGE_KEY = 'chordracer-game-level';

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Map<number, Task>>(new Map());
  const [taskEnd, setTaskEnd] = useState(0);
  const [taskIndex, setTaskIndex] = useState(0);

  const [exp, setExp] = useState(0);
  const [level, setLevel] = useState(() => {
    const savedLevel = localStorage.getItem(LEVEL_STORAGE_KEY);
    return savedLevel ? parseInt(savedLevel, 10) : 1;
  });

  const [isLoading, setIsLoading] = useState(false);
  const initialLoadRef = useRef(false);

  const lastExpDropTime = useRef(Date.now());
  const lastTaskCloseTime = useRef(Date.now());

  const { userId, setLevelCompleted, updateUser } = useUserContext();
  const { activeNotes: midiActiveNotes } = useMidiContext();
  const { activeNotes: virtualActiveNotes, clearClickNotes } = useVirtualKeyboard();
  const activeNotes = useMemo(
    () => [...midiActiveNotes, ...virtualActiveNotes],
    [midiActiveNotes, virtualActiveNotes]
  );

  const { isAutoLevelUpEnabled } = useSettings();
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false); // Add this line

  const setCurrentTaskStartTime = useCallback(() => {
    setTasks(produce(draft => {
      const currentTask = draft.get(taskIndex);
      if (currentTask && currentTask.startTime <= 0) {
        currentTask.startTime = Date.now();
      }
    }));
  }, [taskIndex]);

  const setCurrentTaskClosed = useCallback(() => {
    setTasks(produce(draft => {
      const currentTask = draft.get(taskIndex);
      if (currentTask) {
        currentTask.isClosed = true;
        lastTaskCloseTime.current = Date.now();
      }
    }));
    clearClickNotes();
  }, [taskIndex, clearClickNotes]);

  const setCurrentTaskFailed = useCallback(() => {
    setTasks(produce(draft => {
      const currentTask = draft.get(taskIndex);
      if (currentTask) {
        currentTask.isFailed = true;
      }
    }));
  }, [taskIndex]);

  const handleLevelChange = useCallback((newLevel: number) => {
    if (newLevel < 1) newLevel = 1;
    if (newLevel > 100) newLevel = 100;
    if (newLevel === level) return;

    logger.debug({ newLevel }, "Level changed");

    setHasTriggeredConfetti(false); // Reset confetti flag when level changes

    setLevel(newLevel);
    setExp(0);
    setTasks(new Map());
    setTaskIndex(0);
    setTaskEnd(0);
    setIsLoading(false);
    localStorage.setItem(LEVEL_STORAGE_KEY, newLevel.toString());
    initialLoadRef.current = false;
  }, [level]);

  // Confetti function
  const triggerConfetti = useCallback(() => {
    // Create a burst of confetti from the center
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Add a second burst with different colors
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff']
      });
    }, 200);

    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff']
      });
    }, 400);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      logger.debug({ key: event.key, level }, 'Key pressed');
      if (event.key === 'ArrowUp') {
        logger.debug({ newLevel: level + 1 }, 'Level up');
        handleLevelChange(level + 1);
      } else if (event.key === 'ArrowDown') {
        logger.debug({ newLevel: level - 1 }, 'Level down');
        handleLevelChange(level - 1);
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoading, level, handleLevelChange]); // Add dependencies here

  const fetchTasks = useCallback(async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/v1/tasks/random?level=${level}`);

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const taskData: TaskResponse = await response.json();
      const chordStrings = taskData.chords.split(' ');

      // Update tasks with new tasks at taskEnd position using Immer
      setTasks(produce(draft => {
        chordStrings.forEach((chordString, index) => {
          const position = taskEnd + index;
          draft.set(position, {
            chord: Chord.fromString(chordString),
            taskId: taskData.id,
            chordIndex: index,
            level: taskData.level,
            startTime: -1,
            isFailed: false,
            isClosed: false,
          });

          // Remove old tasks that are no longer needed
          const oldPosition = position - TASK_QUEUE_SIZE;
          if (draft.has(oldPosition)) {
            draft.delete(oldPosition);
          }
        });
      }));


      // Update taskEnd pointer
      setTaskEnd(taskEnd + chordStrings.length);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [level, taskEnd, isLoading]);

  const submitTaskProgress = useCallback(async (task: Task) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const midiInput = activeNotes.map(note => note.number);

      const submission = {
        user_id: userId,
        task_id: task.taskId,
        chord: task.chord.originalSymbol,
        chord_index: task.chordIndex,
        midi_input: midiInput,
        exp: exp + 1,
        local_timestamp: Date.now() / 1000,
      };

      const response = await fetch(`${apiUrl}/v1/submissions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submission),
        credentials: 'include',
      });

      if (!response.ok) {
        logger.error(response.statusText);
        throw new Error('Failed to submit task progress');
      }

      const result = await response.json();
      //logger.debug({ result }, "Task submission successful");

      // If level completed, update completed levels
      if (result.level_completed) {
        setLevelCompleted(result.level_completed);
      }
    } catch (error) {
      console.error('Error submitting task progress:', error);
    }
    if (userId === null) {
      updateUser();
    }
  }, [activeNotes, exp, userId, setLevelCompleted, updateUser]);

  const moveForward = useCallback(() => {
    setTaskIndex(prevIndex => prevIndex + 1);
    setCurrentTaskStartTime();
  }, [setTaskIndex, setCurrentTaskStartTime]);

  // Fetch tasks on initial load
  useEffect(() => {
    if (!initialLoadRef.current) {
      fetchTasks();
      initialLoadRef.current = true;
    }
  }, [fetchTasks]);

  // Fetch tasks when taskIndex is close to the end of the queue
  useEffect(() => {
    const remainingTasks = taskEnd - taskIndex;
    if (remainingTasks < MIN_REMAINING_TASKS && taskEnd > 0) {
      fetchTasks();
    }
  }, [taskIndex, taskEnd, fetchTasks]);

  // Check if player input matches current target chord
  useEffect(() => {
    const currentTask = tasks.get(taskIndex);
    if (!currentTask) {
      return;
    }
    const currentChord = currentTask.chord;
    const notes = new NoteSet(activeNotes.map(note => Note.fromNumber(note.number)));
    if (!currentTask.isClosed && checkChordMatch(currentChord, notes)) {
      submitTaskProgress(currentTask);

      setExp(prev => Math.min(MAX_EXP, prev + 1));
      setCurrentTaskClosed();
    }
  }, [tasks, taskIndex, activeNotes, setCurrentTaskClosed, submitTaskProgress]);

  // Set the start time for the current task
  useEffect(() => {
    setCurrentTaskStartTime();
  }, [tasks, taskIndex, setCurrentTaskStartTime]);

  // Move on to the next task if the current task is closed and no active notes
  useEffect(() => {
    const currentTask = tasks.get(taskIndex);
    if (currentTask && currentTask.isClosed && activeNotes.length === 0) {
      moveForward();
    }
  }, [tasks, taskIndex, activeNotes, moveForward]);

  // Check if the current task is failed
  useEffect(() => {
    const currentTask = tasks.get(taskIndex);
    if (currentTask &&
      // !currentTask.isClosed && // give panalty regardless of task closed.
      activeNotes.length > 0 &&
      activeNotes.some(note => !currentTask.chord.notes.has(Note.fromNumber(note.number % 12)))
    ) {
      setExp(0);
      setCurrentTaskFailed();
    }
  }, [tasks, taskIndex, activeNotes, setCurrentTaskFailed]);

  // continuous exp drop
  useEffect(() => {
    const expDropInterval = setInterval(() => {
      const now = Date.now();
      const deltaTime = (now - Math.max(lastExpDropTime.current, lastTaskCloseTime.current + EXP_FREEZE_DURATION * 1000)) / 1000;
      lastExpDropTime.current = now;
      if (deltaTime > 0) {
        setExp(prevExp => {
          if (prevExp >= MAX_EXP) {
            return MAX_EXP;
          }
          const expRatio = Math.min(1, prevExp / MAX_EXP);
          const expDropRate = EXP_DROP_RATE_MIN * (1 - expRatio) + EXP_DROP_RATE_MAX * expRatio;
          const newExp = Math.max(0, prevExp - expDropRate * deltaTime);
          return newExp;
        });
      }
    }, ANIMATION_INTERVAL_MS);
    return () => clearInterval(expDropInterval);
  }, [exp]);

  // Handle level up
  useEffect(() => {
    if (exp >= MAX_EXP) {
      setLevelCompleted(level);
      if (isAutoLevelUpEnabled) {
        setLevel(prevLevel => prevLevel + 1);
        setExp(0);
        setTaskEnd(taskIndex + 2);
      }

      setTimeout(() => {
        if (!hasTriggeredConfetti) {
          triggerConfetti();
          setHasTriggeredConfetti(true); // Set the flag to prevent multiple triggers
        }
      }, 100);
    }
  }, [level, exp, taskIndex, setLevelCompleted, triggerConfetti, hasTriggeredConfetti, isAutoLevelUpEnabled]);

  return (
    <GameContext.Provider
      value={{
        tasks,
        taskEnd,
        taskIndex,
        exp,
        level,
        moveForward,
        setLevel: handleLevelChange,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = (): GameContextType => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};
